import { useEffect, useRef, useState } from 'react';

/** Desktop hero scroll indicator with fade/translate and chevron loop animation. */
const SCROLL_CHEVRON_OFFSET_PX = 60;
const SCROLL_CHEVRON_FLOOR_TRANSLATE_PX = 40;
const HERO_BANNER_SELECTOR = 'section[data-testid="hero-banner"]';
const ELEMENT_NODE_TYPE = 1;

const ChevronDownSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M8 12L16 20L24 12"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const HeroScrollIndicator = () => {
    const [scrollY, setScrollY] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const indicatorRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const updateWindowMetrics = () => {
            setScrollY(window.scrollY);
            setViewportHeight(window.innerHeight);
        };

        updateWindowMetrics();
        window.addEventListener('scroll', updateWindowMetrics, {
            passive: true,
        });
        window.addEventListener('resize', updateWindowMetrics);

        return () => {
            window.removeEventListener('scroll', updateWindowMetrics);
            window.removeEventListener('resize', updateWindowMetrics);
        };
    }, []);

    const scrollToNextSection = () => {
        const heroSection = indicatorRef.current?.closest(HERO_BANNER_SELECTOR);
        const nextSection = heroSection?.nextElementSibling;

        if (
            nextSection?.nodeType === ELEMENT_NODE_TYPE &&
            'scrollIntoView' in nextSection
        ) {
            nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const heroSection = indicatorRef.current?.closest(HERO_BANNER_SELECTOR);
    const heroHeight =
        heroSection?.nodeType === ELEMENT_NODE_TYPE
            ? heroSection.getBoundingClientRect().height
            : 0;
    const heightDifference =
        heroHeight > viewportHeight ? heroHeight - viewportHeight : 0;
    const scrollMax = heightDifference + SCROLL_CHEVRON_OFFSET_PX;
    const clampedScrollY = Math.min(scrollY, scrollMax);
    const chevronOpacity = scrollMax > 0 ? 1 - clampedScrollY / scrollMax : 1;
    const chevronTranslateY =
        scrollY < scrollMax
            ? -scrollMax + scrollY
            : -SCROLL_CHEVRON_FLOOR_TRANSLATE_PX;

    return (
        <>
            <button
                ref={indicatorRef}
                type="button"
                onClick={scrollToNextSection}
                aria-label="Scroll to next section"
                className="absolute bottom-0 left-1/2 z-20 inline-flex flex-col items-center text-white transition-[transform,opacity] duration-500"
                style={{
                    transform: `translate(-50%, ${chevronTranslateY}px)`,
                    opacity: chevronOpacity,
                    willChange: 'transform, opacity',
                }}
            >
                <span
                    className="font-display text-[0.865rem] font-medium uppercase leading-[0.9]"
                    style={{ letterSpacing: '-0.0259rem' }}
                >
                    Scroll
                </span>
                <ChevronDownSvg
                    style={{
                        width: '2rem',
                        height: '2rem',
                        marginTop: '0.14rem',
                        animation:
                            'hdeChevronUpDown 1s infinite alternate ease-in-out',
                    }}
                />
            </button>
            <style>{`
                @keyframes hdeChevronUpDown {
                    from {
                        transform: translateY(0);
                    }
                    to {
                        transform: translateY(0.625rem);
                    }
                }
            `}</style>
        </>
    );
};
